<!--
Copyright 2024 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license, please see LICENSE.md in the
project root for license information or contact permission@sei.cmu.edu for full terms.
-->

<div class="cssLayoutRowStartCenter">
  <div class="sp-icon">
    <mat-icon class="mdi-24px" fontIcon="mdi-format-list-group" [ngStyle]="{'color': 'var(--mat-sys-primary)'}"></mat-icon>
  </div>
  <mat-form-field style="width: 300px; margin-left: 10px;">
    <input matInput [formControl]="filterControl" placeholder="Search" />
    @if (filterString) {
    <button mat-icon-button matSuffix (click)="filterControl.setValue('')" title="Clear Search">
      <mat-icon style="transform: scale(0.85)" fontIcon="mdi-close-circle-outline"></mat-icon>
    </button>
    }
  </mat-form-field>
  <div class="button-end">
    <mat-paginator #paginator [pageIndex]="0" [pageSize]="20"
      [pageSizeOptions]="[5, 10, 15, 20, 25, 50, 100, 200]"></mat-paginator>
  </div>
</div>

<section class="scrolling-region">
  <table mat-table #injectTypeTable [dataSource]="injectTypeDataSource" matSort (matSortChange)="sortChanged($event)" multiTemplateDataRows>
    <!-- action column -->
    <ng-container matColumnDef="action">
      <mat-header-cell *matHeaderCellDef class="column-action">
        @if (canEdit) {
        <button mat-icon-button (click)="addOrEditInjectType(null)" style="outline: none;"
          title="Add new inject type">
          <mat-icon class="mdi-24px self-center" fontIcon="mdi-plus-circle"></mat-icon>
        </button>
        <button mat-icon-button title="Upload Inject Types from JSON" style="outline: none;"
          (click)="jsonInput.click()">
          <mat-icon class="mdi-24px" fontIcon="mdi-upload"></mat-icon>
        </button>
        <button mat-icon-button title="Download Inject Types as JSON" style="outline: none;"
          (click)="openDownloadDialog()">
          <mat-icon class="mdi-24px" fontIcon="mdi-download"></mat-icon>
        </button>
        } &nbsp;
      </mat-header-cell>
      <mat-cell *matCellDef="let element" class="column-action">
        <div class="action-div">
          @if (canEdit || msel.hasRole(loggedInUserId, null).owner) {
          <button mat-icon-button class="msel-action" (click)="addOrEditInjectType(element); $event.stopPropagation();"
            title="Edit {{ element.name }} injectType">
            <mat-icon class="mdi-18px" fontIcon="mdi-square-edit-outline"
              title="Edit {{ element.name }} injectType"></mat-icon>
          </button>
          <button mat-icon-button (click)="copyInjectType(element); $event.stopPropagation();"
            title="Copy {{ element.name }}">
            <mat-icon class="mdi-18px" fontIcon="mdi-content-copy" alt="Copy"></mat-icon>
          </button>
          <button mat-icon-button class="msel-action" (click)="deleteInjectType(element); $event.stopPropagation();"
            title="Delete {{ element.name }} injectType">
            <mat-icon class="mdi-18px" fontIcon="mdi-trash-can-outline"
              title="Delete {{ element.name }} injectType"></mat-icon>
          </button>
          }
        </div>
      </mat-cell>
    </ng-container>
    <!-- name Column -->
    <ng-container matColumnDef="name">
      <mat-header-cell title="Inject Type Name" *matHeaderCellDef mat-sort-header class="column-name">
        Name
      </mat-header-cell>
      <mat-cell *matCellDef="let element" class="column-name">
        {{ element.name }}
      </mat-cell>
    </ng-container>
    <!-- description Column -->
    <ng-container matColumnDef="description">
      <mat-header-cell title="Inject Type Description" *matHeaderCellDef mat-sort-header class="column-description">
        Description
      </mat-header-cell>
      <mat-cell *matCellDef="let element" class="column-description">
        {{ element.description }}
      </mat-cell>
    </ng-container>
    <!-- Expanded Content Column - The detail row is made up of this one column that spans across all columns -->
    <ng-container matColumnDef="expandedDetail">
      <mat-cell class="expanded-detail-cell" *matCellDef="let element" [attr.colspan]="displayedColumns.length">
        <div [@detailExpand]="element.id == expandedElementId ? 'expanded' : 'collapsed'" style="overflow: hidden">
          @if (expandedElementId === element.id) {
          <mat-expansion-panel class="expansion-panel">
            <mat-expansion-panel-header class="expansion-panel-header">
              DataFields
            </mat-expansion-panel-header>
            <div class="expanded-detail-div">
              <app-data-field-list [loggedInUserId]="loggedInUserId" [showTemplates]="false"
                [injectTypeId]="element.id"></app-data-field-list>
            </div>
          </mat-expansion-panel>
          }
          @if (expandedElementId === element.id) {
          <mat-expansion-panel class="expansion-panel">
            <mat-expansion-panel-header class="expansion-panel-header">
              Injects
            </mat-expansion-panel-header>
            <div class="expanded-detail-div">
              <app-inject-list [canEdit]="canEdit" [loggedInUserId]="loggedInUserId" [catalog]="{}" [injectType]="element"
                [isEditMode]="true"></app-inject-list>
            </div>
          </mat-expansion-panel>
          }
        </div>
      </mat-cell>
    </ng-container>
    <!-- row definitions -->
    <mat-header-row *matHeaderRowDef="displayedColumns"></mat-header-row>
    <mat-row *matRowDef="let row; columns: displayedColumns;" matRipple [class]="getRowClass(row.id)"
      (click)="rowClicked(row)">
    </mat-row>
    <mat-row *matRowDef="let row; columns: ['expandedDetail']" class="detail-row">
    </mat-row>
  </table>
  <!-- No Results Message -->
  @if (injectTypeDataSource?.filteredData.length === 0) {
  <div class="no-results">No Inject Types found</div>
  }
</section>

<input hidden type="file" #jsonInput accept=".json" (change)="selectFile($event)" />
